/**
 * IpoFilters — the shared filter row (Industry · Use Case · Asset · Snapshot
 * Date) for the Impact page's Industry Pipeline Overview chart card, mirroring
 * MotionsPage/MotionFilters.tsx in shape:
 *
 *   - Each field is a Cloudscape control (Select / DatePicker) wrapped in
 *     the shared <FloatingLabelField> for a notched top-border label.
 *   - Layout is flex-wrap; each cell has flex share + min/max-width.
 *   - Fully controlled by the parent (ChartCard owns state + callbacks).
 *
 * The Use Case / Asset dropdowns disable based on the current viewMode
 * (they only apply to certain views); the parent supplies the disabled flag
 * indirectly via the option list + viewMode prop.
 *
 * `rightSlot` is a caller-owned slot for the chart-card's non-filter controls
 * (Chart/Table toggle, + Add / − Delete Chart button) — these render at the
 * right edge of the same visual row per Figma but aren't semantically
 * "filters", so they stay in the parent's markup and get slotted in here.
 */
import { DatePicker, Select, type SelectProps } from '@amzn/awsui-components-react';
import FloatingLabelField from '../../../components/FloatingLabelField/FloatingLabelField';
import type { ReactNode } from 'react';

/** Filter-row cell: shared FloatingLabelField + IPO row-cell layout class
 *  (.ivt-ipo__field — flex share / min-width / max-width). */
function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <FloatingLabelField label={label} className="ivt-ipo__field">
      {children}
    </FloatingLabelField>
  );
}

/**
 * DatePicker's `isDateEnabled` closure (parent-supplied). Returns true iff
 * the picker cell should be selectable. Left generic so a caller could pass
 * something other than "in available set" if the semantic ever widens.
 */
export type IsDateEnabled = (date: Date) => boolean;

export interface IpoFiltersProps {
  industryOpt: SelectProps.Option;
  onIndustryChange: (opt: SelectProps.Option) => void;
  industryOptions: SelectProps.Options;

  ucOpt: SelectProps.Option;
  onUcChange: (opt: SelectProps.Option) => void;
  ucOptions: SelectProps.Options;
  /** UC dropdown is meaningless in Industry / Account views. */
  ucDisabled: boolean;

  assetOpt: SelectProps.Option;
  onAssetChange: (opt: SelectProps.Option) => void;
  assetOptions: SelectProps.Options;
  /** Asset dropdown only applies in Asset view. */
  assetDisabled: boolean;

  snapshotDate: string | undefined;
  onSnapshotDateChange: (date: string | undefined) => void;
  isDateEnabled: IsDateEnabled;
  /** Placeholder shown while the first response is landing; typically the
   *  DB's live snapshotDate echoed by the last GetPipelineBubbles response. */
  snapshotPlaceholder: string;

  /** Left-side slot rendered as the FIRST cell of the row (before Industry).
   *  Impact page uses this for the Pipeline类型 PropertyFilter so it sits
   *  inline with the other filter cells — see Figma node 3186-43468. */
  leftSlot?: ReactNode;

  /** Right-side slot for the parent's chart-card controls (Chart/Table toggle,
   *  + Add / − Delete Chart). Rendered at the right edge of the filter row. */
  rightSlot?: ReactNode;
}

export default function IpoFilters({
  industryOpt,
  onIndustryChange,
  industryOptions,
  ucOpt,
  onUcChange,
  ucOptions,
  ucDisabled,
  assetOpt,
  onAssetChange,
  assetOptions,
  assetDisabled,
  snapshotDate,
  onSnapshotDateChange,
  isDateEnabled,
  snapshotPlaceholder,
  leftSlot,
  rightSlot,
}: IpoFiltersProps) {
  return (
    <div className="ivt-ipo__filter-row">
      {leftSlot}
      <FilterField label="Industry">
        <Select
          selectedOption={industryOpt}
          onChange={(e) => onIndustryChange(e.detail.selectedOption)}
          options={industryOptions}
          placeholder="All"
          expandToViewport
        />
      </FilterField>
      <FilterField label="Use Case">
        <Select
          selectedOption={ucOpt}
          onChange={(e) => onUcChange(e.detail.selectedOption)}
          options={ucOptions}
          placeholder="All"
          disabled={ucDisabled}
          expandToViewport
        />
      </FilterField>
      <FilterField label="Asset">
        <Select
          selectedOption={assetOpt}
          onChange={(e) => onAssetChange(e.detail.selectedOption)}
          options={assetOptions}
          placeholder="All"
          disabled={assetDisabled}
          expandToViewport
        />
      </FilterField>
      <FilterField label="Snapshot Date">
        <DatePicker
          value={snapshotDate ?? ''}
          onChange={(e) => onSnapshotDateChange(e.detail.value)}
          isDateEnabled={isDateEnabled}
          placeholder={snapshotPlaceholder}
          expandToViewport
        />
      </FilterField>
      {rightSlot}
    </div>
  );
}
